import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { CasinoConfigType } from '../../../api/fetchers/casinoConfig/types';
import type { EnvConfigType } from '../../../api/fetchers/env/types';
import type { PlayerConfigType } from '../../../api/fetchers/playerConfig/types';
import type { RtpType } from '../../../api/fetchers/rtp/types';
import type { SessionBalanceResponse } from '../../../api/fetchers/sessionBalance/types';
import type { StatsSettingsType } from '../../../api/fetchers/settings/types';
import type { StatsConfigType } from '../../../api/fetchers/stats/types';
import type { TableConfigType } from '../../../api/fetchers/tableConfig/types';
import type { RealityCheckType, VersionsConfigType } from '../../../api/fetchers/versions/types';
import { IUserSession, UserSession } from '../../../config/UserSession';
import { Logger } from '../../../logger/Logger';
import { SessionStorage } from '../../../storage/session/SessionStorage';
import { fetchCasinoConfigAction } from '../../apiActions/fetchCasinoConfigAction';
import { fetchEnvAction } from '../../apiActions/fetchEnvAction';
import { fetchPlayerConfigAction } from '../../apiActions/fetchPlayerConfigAction';
import { fetchRtpAction } from '../../apiActions/fetchRtpAction';
import { fetchStatsConfigAction } from '../../apiActions/fetchStatsConfigAction';
import { fetchStatsSettingsAction } from '../../apiActions/fetchStatsSettingsAction';
import { fetchTableConfigAction } from '../../apiActions/fetchTableConfigAction';
import { fetchVersionsAction } from '../../apiActions/fetchVersionsAction';

import { CoreDefaultTimings, type ICoreTimings, type PlayAreaDimensions } from './types';

export const configSlice = createSlice({
  name: 'config',
  initialState: (): {
    session?: IUserSession | null;
    urlParams: string;
    playAreaSize: number;
    playAreaDimensions: PlayAreaDimensions | null;
    tableConfig: TableConfigType | null;
    statsSettings: StatsSettingsType | null;
    statsConfig: StatsConfigType | null;
    casinoConfig: CasinoConfigType | null;
    env: EnvConfigType | null;
    playerConfig: PlayerConfigType | null;
    versions: VersionsConfigType | null;
    realityCheck: RealityCheckType | null;
    rtp: RtpType | null;
    timings?: ICoreTimings;
    defaultTimings?: ICoreTimings;
    gameHeaderOverlayHeight?: number;
    isGameVisible: boolean;
  } => {
    return {
      session: UserSession.getSession(),
      urlParams: window.location.search,
      playAreaSize: 0, // TODO: deprecate me and use value bellow
      playAreaDimensions: null,
      tableConfig: null,
      statsConfig: null,
      statsSettings: null,
      casinoConfig: null,
      env: null,
      playerConfig: null,
      versions: null,
      realityCheck: null,
      rtp: null,
      timings: undefined,
      defaultTimings: undefined,
      gameHeaderOverlayHeight: 0,
      isGameVisible: true,
    };
  },
  reducers: {
    setPlayAreaSize: (state, action: PayloadAction<number>) => {
      state.playAreaSize = action.payload;
    },
    setPlayAreaDimensions: (state, action: PayloadAction<PlayAreaDimensions>) => {
      state.playAreaDimensions = action.payload;
    },
    setScreenName: (state, action: PayloadAction<string>) => {
      state.playerConfig!.screenName = action.payload;
    },
    setRealityCheck: (state, action: PayloadAction<RealityCheckType | null>) => {
      state.realityCheck = action.payload;
    },
    sessionBalanceWasSet: (state) => {
      state.playerConfig!.compliance.sessionBalanceWasSet = true;
    },
    setAaamsSessions: (state, action: PayloadAction<SessionBalanceResponse>) => {
      state.tableConfig!.aamsTicket = action.payload.aamsTicket;
      state.tableConfig!.aamsSessionId = action.payload.aamsSessionId;
    },
    setGameServer: (
      state,
      action: PayloadAction<{
        httpAddress: string;
        wsAddress: string;
      }>
    ) => {
      if (state.tableConfig) {
        state.tableConfig.wsAddress = action.payload.wsAddress;
        state.tableConfig.gsAddress = action.payload.httpAddress;
      }
      if (state.session) {
        state.session.actualWebServer = action.payload.httpAddress;
        state.session.socketServer = action.payload.wsAddress;
      }

      const savedSession = SessionStorage.getJson<IUserSession>('PPG');
      if (savedSession) {
        if (savedSession.actual_web_server && savedSession.socket_server) {
          SessionStorage.setJson('PPG', {
            ...savedSession,
            actual_web_server: action.payload.httpAddress,
            socket_server: action.payload.wsAddress,
          });
        }
      }

      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.get('actual_web_server') && searchParams.get('socket_server')) {
        searchParams.set('actual_web_server', action.payload.httpAddress);
        searchParams.set('socket_server', action.payload.wsAddress);
      }
    },
    setDefaultTimings: (state, action: PayloadAction<Partial<ICoreTimings>>) => {
      if (!state.defaultTimings) {
        const combinedTimings = {
          ...CoreDefaultTimings,
          ...action.payload,
        };
        state.defaultTimings = combinedTimings;
        state.timings = combinedTimings;
      }
    },
    setTimings: (state, action: PayloadAction<Partial<ICoreTimings>>) => {
      if (state.timings) {
        state.timings = {
          ...state.timings,
          ...action.payload,
        };
      } else {
        console.error('Setting timings before defining default timings');
      }
    },
    resetTimings: (state, action: PayloadAction<void | (keyof ICoreTimings)[]>) => {
      if (state.timings) {
        if (action.payload) {
          for (const key of action.payload) {
            if (state.defaultTimings) {
              state.timings[key] = state.defaultTimings[key];
            }
          }
        } else {
          state.timings = state.defaultTimings;
        }
      } else {
        console.error('Resetting timings before defining default timings');
      }
    },
    setGameHeaderOverlayHeight: (state, action: PayloadAction<number>) => {
      state.gameHeaderOverlayHeight = action.payload;
    },
    /** Is the game window/tab visible */
    setGameVisible: (state, action: PayloadAction<boolean>) => {
      state.isGameVisible = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTableConfigAction.fulfilled, (state, action) => {
      state.tableConfig = action.payload;
    });
    builder.addCase(fetchStatsSettingsAction.fulfilled, (state, action) => {
      state.statsSettings = action.payload;
    });
    builder.addCase(fetchStatsConfigAction.fulfilled, (state, action) => {
      state.statsConfig = action.payload;
    });
    builder.addCase(fetchCasinoConfigAction.fulfilled, (state, action) => {
      state.casinoConfig = action.payload;
      if (action.payload.homeUrl) {
        try {
          new URL(action.payload.homeUrl);
        } catch (error) {
          Logger.logFailure('invalidHomeUrl', {
            additional: action.payload.homeUrl,
          });
        }
      }
    });
    builder.addCase(fetchEnvAction.fulfilled, (state, action) => {
      state.env = action.payload;
    });
    builder.addCase(fetchPlayerConfigAction.fulfilled, (state, action) => {
      state.playerConfig = action.payload;
    });
    builder.addCase(fetchVersionsAction.fulfilled, (state, action) => {
      state.versions = action.payload;
    });
    builder.addCase(fetchRtpAction.fulfilled, (state, action) => {
      state.rtp = action.payload;
    });
  },
});

export const configActions = configSlice.actions;
